import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import LanguageSelector from "../components/LanguageSelector";
import {
  Wrench,
  ClipboardList,
  MessageSquare,
  BarChart3,
  Shield,
  Clock,
  ArrowRight,
  Check,
  Menu,
  X,
  Loader2,
  Smartphone,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API}/public/plans`);
        // Plans are already filtered by show_on_pricing in the API
        // Sort by sort_order first, then by price
        // Filter out test plans (those with 'test' in the id)
        const displayPlans = response.data
          .filter(p => !p.id?.toLowerCase().includes('test'))
          .sort((a, b) => {
            if (a.sort_order !== b.sort_order) return (a.sort_order || 99) - (b.sort_order || 99);
            return (a.price || 0) - (b.price || 0);
          });
        
        if (displayPlans.length > 0) {
          setPlans(displayPlans);
        } else {
          // Use fallback if no plans returned
          setPlans(getDefaultPlans());
        }
      } catch (error) {
        console.error("Failed to fetch plans:", error);
        setPlans(getDefaultPlans());
      } finally {
        setLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  const getDefaultPlans = () => [
    {
      id: "free",
      name: "Free",
      price: 0,
      billing_cycle: "month",
      max_jobs_per_month: 50,
      max_branches: 1,
      max_users: 2,
      features: { whatsapp_messages: true, pdf_job_sheet: true, job_management: true }
    },
    {
      id: "starter",
      name: "Starter",
      price: 499,
      billing_cycle: "month",
      max_jobs_per_month: 200,
      max_branches: 2,
      max_users: 5,
      features: { whatsapp_messages: true, pdf_job_sheet: true, job_management: true, photo_upload: true }
    },
    {
      id: "pro",
      name: "Pro",
      price: 999,
      billing_cycle: "month",
      max_jobs_per_month: -1,
      max_branches: 5,
      max_users: 15,
      features: { whatsapp_messages: true, pdf_job_sheet: true, inventory_management: true, advanced_analytics: true, priority_support: true }
    },
    {
      id: "enterprise",
      name: "Enterprise",
      price: -1,
      billing_cycle: "month",
      max_jobs_per_month: -1,
      max_branches: -1,
      max_users: -1,
      features: { api_access: true, dedicated_account_manager: true, custom_branding: true }
    }
  ];

  const formatPrice = (plan) => {
    if (plan.price === 0) return "Free";
    if (plan.price === -1 || plan.price === null) return "Custom";
    return `₹${plan.price}`;
  };

  const formatPeriod = (plan) => {
    if (plan.price === 0) return "forever";
    if (plan.price === -1 || plan.price === null) return "contact us";
    return `/${plan.billing_cycle || "month"}`;
  };

  const getPlanFeatures = (plan) => {
    const features = [];
    
    // Add limits from API
    if (plan.max_users === -1) {
      features.push("Unlimited Team members");
    } else if (plan.max_users) {
      features.push(`${plan.max_users} Team member${plan.max_users > 1 ? 's' : ''}`);
    }
    
    if (plan.max_branches === -1) {
      features.push("Unlimited Branches");
    } else if (plan.max_branches) {
      features.push(`${plan.max_branches} Branch${plan.max_branches > 1 ? 'es' : ''}`);
    }
    
    if (plan.max_jobs_per_month === -1) {
      features.push("Unlimited Jobs/month");
    } else if (plan.max_jobs_per_month) {
      features.push(`${plan.max_jobs_per_month} Jobs/month`);
    }
    
    // Add feature flags from API
    if (plan.features?.whatsapp_messages) features.push("WhatsApp notifications");
    if (plan.features?.pdf_job_sheet) features.push("PDF Job sheets");
    if (plan.features?.qr_tracking) features.push("QR Code tracking");
    if (plan.features?.inventory_management) features.push("Inventory management");
    if (plan.features?.advanced_analytics) features.push("Advanced analytics");
    if (plan.features?.technician_metrics) features.push("Technician metrics");
    if (plan.features?.customer_management) features.push("Customer management");
    if (plan.features?.multi_branch) features.push("Multi-branch support");
    if (plan.features?.custom_branding) features.push("Custom branding");
    if (plan.features?.data_export) features.push("Data export (CSV/Excel)");
    if (plan.features?.priority_support) features.push("Priority support");
    if (plan.features?.api_access) features.push("API access");
    // Used device trading - friendly labels
    if (plan.features?.mobile_phone_trading || plan.features?.it_equipment_trading) {
      features.push("Used device buy/sell tracking");
    }
    
    // Limit to 8 features max for display
    return features.slice(0, 8);
  };

  const getCta = (plan) => {
    if (plan.price === 0) return "Start Free";
    if (plan.price === -1 || plan.price === null) return "Contact Sales";
    return `Get ${plan.name}`;
  };

  const isPopular = (plan) => {
    // Only mark as popular if explicitly set, or if it's the Pro plan
    return plan.is_featured || plan.badge || plan.name?.toLowerCase() === "pro";
  };

  const features = [
    {
      icon: ClipboardList,
      title: t("landing.features.job_management.title"),
      description: t("landing.features.job_management.description"),
    },
    {
      icon: MessageSquare,
      title: t("landing.features.whatsapp.title"),
      description: t("landing.features.whatsapp.description"),
    },
    {
      icon: BarChart3,
      title: t("landing.features.analytics.title"),
      description: t("landing.features.analytics.description"),
    },
    {
      icon: Shield,
      title: t("landing.features.dispute_prevention.title"),
      description: t("landing.features.dispute_prevention.description"),
    },
    {
      icon: Clock,
      title: t("landing.features.status_timeline.title"),
      description: t("landing.features.status_timeline.description"),
    },
    {
      icon: Wrench,
      title: t("landing.features.multi_branch.title"),
      description: t("landing.features.multi_branch.description"),
    },
  ];

  // All features for comprehensive display
  const allFeatures = [
    { icon: "📱", title: "Mobile Phone Trading", description: "Buy & sell used smartphones with complete seller records, IMEI tracking, condition grading, and profit margins." },
    { icon: "💻", title: "IT Equipment Trading", description: "Trade laptops, tablets, and accessories. Track serial numbers, specifications, and resale value." },
    { icon: "📋", title: "Job Management", description: "Track repairs from intake to delivery with status updates, technician assignment, and time tracking." },
    { icon: "💬", title: "WhatsApp Integration", description: "Send automated status updates, approval requests, and delivery notifications to customers." },
    { icon: "👥", title: "Customer CRM", description: "Complete customer database with device history, ledger, credit management, and communication logs." },
    { icon: "📦", title: "Inventory Management", description: "Track spare parts, set reorder alerts, manage stock levels, and link parts to repairs." },
    { icon: "🏢", title: "Multi-Branch Support", description: "Manage multiple shop locations from one dashboard with branch-wise reporting." },
    { icon: "📄", title: "PDF Job Sheets", description: "Generate professional job sheets and invoices with QR codes for customer tracking." },
    { icon: "💰", title: "Profit Tracking", description: "Track revenue, expenses, parts cost, labor cost, and profit margins per job." },
    { icon: "👨‍💼", title: "Team Management", description: "Role-based access control for admin, technicians, and staff with activity logs." },
    { icon: "🔍", title: "IMEI/Serial Tracking", description: "Record device identifiers, warranty status, and prevent duplicate entries." },
    { icon: "📊", title: "Reports & Analytics", description: "Revenue reports, technician performance, customer insights, and business trends." },
    { icon: "💳", title: "Customer Ledger", description: "Track outstanding balances, payment history, and offer credit to regular customers." },
    { icon: "🌐", title: "Customer Self-Tracking", description: "Shareable tracking links let customers check repair status without calling." },
    { icon: "💱", title: "Multi-Currency Support", description: "Set your shop's currency - INR, USD, EUR, GBP, AED, and 45+ currencies supported." },
    { icon: "📱", title: "GST/Tax Compliant", description: "Generate tax-compliant invoices with proper GST/VAT calculations." },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Wrench className="w-5 h-5 text-primary-foreground" />
              </div>
              <span className="text-xl font-bold tracking-tight">
                aftersales<span className="text-primary">.pro</span>
              </span>
            </div>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-muted-foreground hover:text-foreground transition-colors">
                {t("landing.footer.features")}
              </a>
              <a href="#pricing" className="text-muted-foreground hover:text-foreground transition-colors">
                {t("landing.footer.pricing")}
              </a>
              <LanguageSelector variant="ghost" />
              <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn">
                {t("auth.login.submit")}
              </Button>
              <Button onClick={() => navigate("/signup")} data-testid="nav-signup-btn">
                {t("landing.cta.button")}
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border">
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-muted-foreground hover:text-foreground">
                  {t("landing.footer.features")}
                </a>
                <a href="#pricing" className="text-muted-foreground hover:text-foreground">
                  {t("landing.footer.pricing")}
                </a>
                <div className="flex items-center gap-2">
                  <LanguageSelector showLabel />
                </div>
                <Button variant="ghost" onClick={() => navigate("/login")} className="justify-start">
                  {t("auth.login.submit")}
                </Button>
                <Button onClick={() => navigate("/signup")}>{t("landing.cta.button")}</Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="hero-gradient absolute inset-0" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
                {t("landing.hero_badge")}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-balance">
                {t("landing.hero.title").split(",")[0]},{" "}
                <span className="text-primary">{t("landing.hero.title").split(",")[1] || "simplified"}</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl">
                {t("landing.hero.subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  className="btn-hover"
                  onClick={() => navigate("/signup")}
                  data-testid="hero-start-trial-btn"
                >
                  {t("landing.hero.cta")}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate("/login")}
                  data-testid="hero-login-btn"
                >
                  {t("landing.hero.cta_login")}
                </Button>
              </div>
              <div className="flex items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  {t("landing.no_credit_card")}
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  {t("landing.setup_time")}
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1676630444903-163fe485c5d1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxlbGVjdHJvbmljcyUyMHJlcGFpciUyMHRlY2huaWNpYW4lMjB3b3JraW5nfGVufDB8fHx8MTc2ODQxNDc1N3ww&ixlib=rb-4.1.0&q=85"
                  alt="Technician working on electronics"
                  className="w-full aspect-[4/3] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{t("landing.active_jobs")}</p>
                        <p className="text-2xl font-bold">24</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{t("landing.completed_today")}</p>
                        <p className="text-2xl font-bold text-green-600">8</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{t("landing.pending_delivery")}</p>
                        <p className="text-2xl font-bold text-orange-500">5</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              {t("landing.features.title")}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("landing.features.subtitle")}
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card
                key={index}
                className="card-interactive animate-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Used Device Trading Module */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Smartphone className="w-4 h-4" />
              Trading Module
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Buy & Sell Used Devices
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Expand your revenue by trading used mobile phones and IT equipment. Track inventory, margins, and seller details all in one place.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Mobile Phone Trading */}
            <Card className="border-2 border-blue-200 bg-white">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                    <span className="text-3xl">📱</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">Mobile Phone Trading</h3>
                    <p className="text-muted-foreground">Buy & sell smartphones</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {[
                    "IMEI number tracking & verification",
                    "Device condition grading (A/B/C/D)",
                    "Seller ID proof & contact storage",
                    "Purchase price & selling price tracking",
                    "Profit margin calculation per device",
                    "Stock inventory with photos",
                    "Password-protected margin visibility",
                    "Declined intake logging with reasons"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* IT Equipment Trading */}
            <Card className="border-2 border-purple-200 bg-white">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                    <span className="text-3xl">💻</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">IT Equipment Trading</h3>
                    <p className="text-muted-foreground">Laptops, tablets & accessories</p>
                  </div>
                </div>
                <ul className="space-y-3">
                  {[
                    "Serial number & model tracking",
                    "Specification details (RAM, Storage, etc.)",
                    "Condition assessment & grading",
                    "Seller documentation & records",
                    "Purchase & resale price management",
                    "Profit tracking per equipment",
                    "Category-wise inventory",
                    "Complete transaction history"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-700">
                      <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* All Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Everything You Need to Run Your Repair Shop
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A complete suite of tools designed specifically for mobile, laptop, and electronics repair businesses.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {allFeatures.map((feature, index) => (
              <Card key={index} className="bg-muted/30 border-0 hover:bg-muted/50 transition-colors">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{feature.icon}</span>
                    <div>
                      <h4 className="font-semibold text-sm mb-1">{feature.title}</h4>
                      <p className="text-xs text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t("landing.how_it_works.title")}</h2>
            <p className="text-lg text-muted-foreground">{t("landing.how_it_works.subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "1", title: t("landing.how_it_works.step1_title"), desc: t("landing.how_it_works.step1_desc") },
              { step: "2", title: t("landing.how_it_works.step2_title"), desc: t("landing.how_it_works.step2_desc") },
              { step: "3", title: t("landing.how_it_works.step3_title"), desc: t("landing.how_it_works.step3_desc") },
              { step: "4", title: t("landing.how_it_works.step4_title"), desc: t("landing.how_it_works.step4_desc") },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t("landing.pricing.title")}</h2>
            <p className="text-lg text-muted-foreground">{t("landing.pricing.subtitle")}</p>
          </div>
          {loadingPlans ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="flex justify-center">
              <div className={`grid gap-6 ${plans.length <= 2 ? 'md:grid-cols-2 max-w-3xl' : plans.length === 3 ? 'md:grid-cols-3 max-w-5xl' : plans.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4 max-w-6xl' : 'md:grid-cols-2 lg:grid-cols-3 max-w-6xl'} mx-auto`}>
              {plans.map((plan, index) => (
                <Card
                  key={plan.id || index}
                  className={`relative h-full flex flex-col ${isPopular(plan) ? 'border-primary border-2 shadow-xl' : 'border-border shadow-md'}`}
                >
                  {isPopular(plan) && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                      <span className="bg-primary text-primary-foreground text-sm font-semibold px-4 py-1.5 rounded-full shadow-lg whitespace-nowrap">
                        {plan.badge || "Most Popular"}
                      </span>
                    </div>
                  )}
                  <CardContent className="p-6 pt-8 flex flex-col flex-1">
                    <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                    <p className="text-muted-foreground text-sm mb-4">{plan.description || (plan.price === 0 ? "Perfect for getting started" : "For growing businesses")}</p>
                    <div className="mb-6">
                      {plan.original_price && plan.original_price > plan.price && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-lg text-muted-foreground line-through">₹{plan.original_price}</span>
                          <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
                            SAVE {Math.round((1 - plan.price / plan.original_price) * 100)}%
                          </span>
                        </div>
                      )}
                      <div className="flex items-baseline gap-1">
                        <span className="text-3xl font-bold">{plan.price === 0 ? 'Free' : `₹${plan.price}`}</span>
                        <span className="text-muted-foreground text-sm">
                          {plan.price === 0 ? 'forever' : plan.billing_cycle === 'yearly' ? '/year' : plan.billing_cycle === 'monthly' ? '/month' : `/${plan.billing_cycle}`}
                        </span>
                      </div>
                      {plan.price > 0 && plan.billing_cycle === 'yearly' && (
                        <p className="text-xs text-muted-foreground mt-1">+ 18% GST • Just ₹{Math.round(plan.price/12)}/month</p>
                      )}
                      {plan.price > 0 && plan.billing_cycle === 'monthly' && (
                        <p className="text-xs text-muted-foreground mt-1">+ 18% GST</p>
                      )}
                    </div>
                    <ul className="space-y-2 mb-6 flex-1">
                      {getPlanFeatures(plan).map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      className="w-full mt-auto"
                      variant={plan.price > 0 ? "default" : "outline"}
                      size="lg"
                      onClick={() => navigate("/signup")}
                      data-testid={`pricing-${plan.name?.toLowerCase().replace(/\s+/g, '-')}-btn`}
                    >
                      {getCta(plan)}
                    </Button>
                  </CardContent>
                </Card>
              ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            {t("landing.cta.title")}
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            {t("landing.cta.subtitle")}
          </p>
          <Button size="lg" onClick={() => navigate("/signup")} data-testid="cta-start-btn">
            {t("landing.cta.button")}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 mb-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-primary-foreground" />
                </div>
                <span className="font-bold">
                  aftersales<span className="text-primary">.pro</span>
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                The complete repair shop management solution for mobile, laptop & electronics repair businesses.
              </p>
            </div>

            {/* Solutions */}
            <div>
              <h4 className="font-semibold mb-4 text-sm">Solutions</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/features/mobile-repair-software" className="text-muted-foreground hover:text-foreground transition-colors">Mobile Repair Software</a></li>
                <li><a href="/features/laptop-repair-software" className="text-muted-foreground hover:text-foreground transition-colors">Laptop Repair Software</a></li>
                <li><a href="/features/multi-branch-repair-software" className="text-muted-foreground hover:text-foreground transition-colors">Multi-Branch Management</a></li>
                <li><a href="/features/whatsapp-repair-software" className="text-muted-foreground hover:text-foreground transition-colors">WhatsApp Integration</a></li>
                <li><a href="/features/used-phone-trading" className="text-muted-foreground hover:text-foreground transition-colors">Used Phone Trading</a></li>
                <li><a href="/features/used-laptop-trading" className="text-muted-foreground hover:text-foreground transition-colors">Used Laptop Trading</a></li>
              </ul>
            </div>

            {/* Regions */}
            <div>
              <h4 className="font-semibold mb-4 text-sm">Regions</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/features/repair-shop-india" className="text-muted-foreground hover:text-foreground transition-colors">India</a></li>
                <li><a href="/features/repair-shop-usa" className="text-muted-foreground hover:text-foreground transition-colors">United States</a></li>
                <li><a href="/features/repair-shop-uk" className="text-muted-foreground hover:text-foreground transition-colors">United Kingdom</a></li>
                <li><a href="/features/repair-shop-uae" className="text-muted-foreground hover:text-foreground transition-colors">UAE & Middle East</a></li>
              </ul>
            </div>

            {/* Compare */}
            <div>
              <h4 className="font-semibold mb-4 text-sm">Compare</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/compare/repairdesk" className="text-muted-foreground hover:text-foreground transition-colors">vs RepairDesk</a></li>
                <li><a href="/compare/repairshopr" className="text-muted-foreground hover:text-foreground transition-colors">vs RepairShopr</a></li>
                <li><a href="/compare/bytephase" className="text-muted-foreground hover:text-foreground transition-colors">vs BytePhase</a></li>
                <li><a href="/compare/servicecircle" className="text-muted-foreground hover:text-foreground transition-colors">vs ServiceCircle</a></li>
                <li><a href="/compare/fixably" className="text-muted-foreground hover:text-foreground transition-colors">vs Fixably</a></li>
              </ul>
            </div>

            {/* Company & Partners */}
            <div>
              <h4 className="font-semibold mb-4 text-sm">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/contact" className="text-muted-foreground hover:text-foreground transition-colors">Contact Us</a></li>
                <li><a href="/legal/privacy-policy" className="text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</a></li>
                <li><a href="/legal/terms-of-service" className="text-muted-foreground hover:text-foreground transition-colors">Terms of Service</a></li>
                <li><a href="/legal/refund-policy" className="text-muted-foreground hover:text-foreground transition-colors">Refund Policy</a></li>
              </ul>
            </div>

            {/* Partner Program */}
            <div>
              <h4 className="font-semibold mb-4 text-sm">Partner Program</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/reseller/register" className="text-muted-foreground hover:text-foreground transition-colors">Become a Reseller</a></li>
                <li><a href="/reseller/login" className="text-muted-foreground hover:text-foreground transition-colors">Reseller Login</a></li>
                <li><span className="text-muted-foreground">Earn 15-20% Commission</span></li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-border pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-muted-foreground">
                {t("landing.footer.copyright")}
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
                <a href="/compare/mhelpdesk" className="hover:text-foreground">vs mHelpDesk</a>
                <a href="/compare/repairq" className="hover:text-foreground">vs RepairQ</a>
                <a href="/compare/cellsmartpos" className="hover:text-foreground">vs CellSmart POS</a>
                <a href="/compare/orderry" className="hover:text-foreground">vs Orderry</a>
                <a href="/compare/repaircms" className="hover:text-foreground">vs RepairCMS</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
